import { Box, Flex, HStack, HTMLChakraProps, Link, Skeleton, Text, TextProps, useDisclosure } from '@chakra-ui/react';
import { MaybeRenderProp } from '@chakra-ui/react-utils';
import { runIfFn } from '@chakra-ui/utils';
import { FC, ReactNode } from 'react';
import Linkify from 'react-linkify';

import { CommentPostedAt } from '@/components/ui/features/lecture/LectureDiscussion/components/CommentPostedAt';
import { CommentReplyButton } from '@/components/ui/features/lecture/LectureDiscussion/components/CommentReplyButton';
import { useAnalytics } from '@/context/Analytics';
import { Comment } from '@/resources/Comment';

import { CommentAuthors } from './CommentAuthors';
import { CommentProfile } from './CommentProfile';
import { CommentUpVote } from './CommentUpvote';

const nestedStyles = ({ isRoot }) => {
  if (isRoot) {
    return {};
  }

  return {
    borderLeft: 'solid 1px',
    borderLeftColor: 'gray.200',
    pl: '20px',
  };
};

interface ICommentListItemProps extends HTMLChakraProps<'div'> {
  lectureId: string;
  comment: Comment;
  isRoot?: boolean;
  menu: ReactNode;
  replies?: MaybeRenderProp<{
    isOpen: boolean;
  }>;
}

export const CommentListItem: FC<ICommentListItemProps> = ({ lectureId, comment, isRoot, menu, replies, ...rest }) => {
  const analytics = useAnalytics();
  const { isOpen, onToggle } = useDisclosure();

  const toggleReplyHandler = () => {
    onToggle();

    analytics.track('library_lecture_comment_reply', { lecture_id: lectureId, comment_id: comment.id });
  };

  return (
    <Box {...nestedStyles({ isRoot })} {...rest}>
      <Flex>
        <Flex flex="1" align="center">
          <CommentProfile comment={comment} postedAt={<CommentPostedAt comment={comment} />} />
        </Flex>
        {menu}
      </Flex>

      <Box pl={14} mt="6px" wordBreak="break-word" whiteSpace="pre-wrap">
        <Text>
          <Linkify
            componentDecorator={(href, text, key) => (
              <Link color="blue.500" href={href} key={key} target="_blank" rel="noopener noreferrer">
                {text}
              </Link>
            )}
          >
            {comment.content}
          </Linkify>
        </Text>

        <HStack mt={2} align="center">
          <CommentUpVote comment={comment} />
          {isRoot && <CommentAuthors comment={comment} />}
          {isRoot && <CommentReplyButton comment={comment} onClick={toggleReplyHandler} />}
        </HStack>

        {runIfFn(replies, { isOpen })}
      </Box>
    </Box>
  );
};

export interface ICommentListItemFallback {
  isRoot: boolean;
}

export const CommentListItemFallback: FC<ICommentListItemFallback> = ({ isRoot, ...rest }) => (
  <Box {...nestedStyles({ isRoot })} {...rest}>
    <Skeleton h="132px" />
  </Box>
);

export interface ICommentListItemEmpty extends TextProps {
  isRoot: boolean;
}

export const CommentListItemEmpty: FC<ICommentListItemEmpty> = ({ isRoot, ...rest }) => (
  <Text color="gray.400" {...nestedStyles({ isRoot })} {...rest} />
);
